import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { CountryCode, WFSSettings, WMSSettings, WMTSSettings } from './explore-map.model';

import { get as getProjection } from 'ol/proj';
import { getTopLeft, getWidth } from 'ol/extent';
import ImageTile from 'ol/ImageTile';
import WMTS from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import TileWMS from 'ol/source/TileWMS';

@Injectable()
export class ExploreMapService {
  constructor(private http: HttpClient) {}

  wmsSource: TileWMS;

  /**
   * Get country code by coordinates
   * @param {any} coordinates
   * @returns {Observable<CountryCode>}
   */
  getLocationCountryCode(coordinates: any): Observable<CountryCode> {
    let params = new HttpParams();
    params = params.append('longitude', coordinates[0]);
    params = params.append('latitude', coordinates[1]);
    return this.http.get<CountryCode>(`${environment.apiBaseURL}/get-location`, {
      params: params,
    });
  }

  /**
   * @param  {any} url
   * @returns Observable
   */
  getTileSourceFeature(url: any): Observable<any> {
    return this.http.get(`${environment.mapBaseURL}/${url}`,  {
      headers: new HttpHeaders({ 'Content-Type': 'text/xml' }),
      responseType: 'text' as 'text',
    });
  }

  getWMTSSource(wmtsSettings: WMTSSettings) {
    let projection = getProjection('EPSG:3857');
    let projectionExtent = projection.getExtent();
    let size = getWidth(projectionExtent) / 256;
    let resolutions = new Array(14);
    let matrixIds = new Array(14);
    for (let z = 0; z < 24; ++z) {
      // generate resolutions and matrixIds arrays for this WMTS
      resolutions[z] = size / Math.pow(2, z);
      matrixIds[z] = z;
    }

    let wmtsSource = new WMTS({
      url: wmtsSettings.url,
      requestEncoding: 'REST',
      layer: wmtsSettings.layerName,
      matrixSet: 'euro_3857',
      format: 'image/png',
      tileGrid: new WMTSTileGrid({
        origin: getTopLeft(projectionExtent),
        resolutions: resolutions,
        matrixIds: matrixIds,
      }),
      style: 'default',
      tileLoadFunction: (imageTile: ImageTile, src: string) => {
        // Use a tileLoadFunction to add authentication headers to the request
        this.getWMTSImage(src).subscribe({
          next: (data) => {
            const blob = new Blob([data], { type: 'image/png' });
            let imageUrl = URL.createObjectURL(blob);
            let img = imageTile.getImage() as HTMLImageElement;
            img.src = imageUrl;
          },
          error: (err) => {
            console.log(err);
          },
          complete: () => {},
        });
      },
    });

    return wmtsSource;
  }

  /**
   * Get WMTS Image
   * @param url
   * @returns {Observable<Blob>}
   */
  getWMTSImage(url: string) {
    return this.http.get(`${environment.mapBaseURL}/${url}`, {
      responseType: 'blob',
    });
  }

  getLegendGraphic(layerName: string): Observable<any> {
    return this.http.get(
      `${environment.mapBaseURL}/maps/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetLegendGraphic&layer=${layerName}&FORMAT=image/png&token=${environment.elsToken}`,
      {
        responseType: 'blob',
      },
    );
  }

  /**
   * Get Feature
   * @param wfsSettings
   * @param extent
   * @returns {Observable<Object>}
   */
  getFeature(wfsSettings: WFSSettings, extent: any): Observable<any> {
    if (wfsSettings.outputFormat === 'application/json') {
      return this.http.get(
        `${wfsSettings.url}&version=${wfsSettings.version}&typeNames=${
          wfsSettings.layerName
        }&srsName=${wfsSettings.srsName}&bbox=${extent.join(
          ',',
        )}&service=WFS&request=GetFeature&outputFormat=application/json`,
      );
    } else {
      // Assume XML/GML
      return this.http.get(
        `${wfsSettings.url}&version=${wfsSettings.version}&typeNames=${
          wfsSettings.layerName
        }&srsName=${wfsSettings.srsName}&bbox=${extent.join(
          ',',
        )}&service=WFS&request=GetFeature&outputFormat=${wfsSettings.outputFormat}`,
        {
          headers: new HttpHeaders({ 'Content-Type': 'text/xml' }),
          responseType: 'text' as 'text',
        },
      );
    }
  }


    /**
   * Add WMS Source
   * WMS source isn't added as a map layer, but is used for the Identify functionality
   * @param wmsSettings
   */
     addWMSSource(wmsSettings: WMSSettings): void {
      this.wmsSource = new TileWMS({
        params: {
          LAYERS: wmsSettings.layerName,
        },
        serverType: 'geoserver',
        crossOrigin: 'anonymous',
        url: wmsSettings.url,
      });
    }


  /**
   * Change layer in WMS source for GetFeature querry
   * @param cadastralLayer
   */
  changeWMSLayer(cadastralLayers: any[]){
    this.wmsSource.updateParams(
      {QUERY_LAYERS: cadastralLayers}
    )
  }
}
